import { ThunkDispatch } from 'redux-thunk';
import _, { get, has } from 'lodash';
import moment from 'moment';
import { Range } from 'react-date-range';
import { v4 as uuidv4 } from 'uuid';

import { IAction, IState } from 'shared/interface/state';
import HttpService from 'shared/services/http.service';
import { IDropdownOptions, IPreSignedData, PresignedFieldKeys } from 'shared/interface';
import { API_CONFIG } from 'shared/constants/api';
import { IMultiEntry } from 'features/createContest/interface/createContest';

/**
 * create action creator
 * @param ACTION - type of action
 * @param data - data
 */
export const createAction = (ACTION: string, data: any = null): IAction => ({
	type: ACTION,
	payload: data
});

/**
 * create loading selector
 * @param actions - actions to dispatch
 */
export const createLoadingSelector = (actions: string[]) => (state: IState) =>
	// returns true only when all actions is not loading
	_(actions).some((action: string) => _.get(state, `loading.api.${action}`));

/**
 * dispatch action after given time (to handle some events like close modal after success api call)
 * @param dispatch - dispatch object
 * @param action - action type
 * @param time - time after which action is to be dispatched (default - 100ms)
 */
export const dispatchActionAfterTime = (
	dispatch: ThunkDispatch<unknown, unknown, IAction>,
	action: string,
	time = 100
) => {
	setTimeout(() => {
		dispatch(createAction(action));
	}, time);
};

export const formatDate = (date: string | number | Date | undefined, format = 'DD MMM, HH:mm') => {
	if (!date) {
		return '';
	}
	return moment(date).format(format);
};

export const debounce = (func: any, wait = 1000) => {
	// eslint-disable-next-line no-undef
	let h: NodeJS.Timeout;
	return (...args: any) => {
		clearTimeout(h);
		h = setTimeout(() => func(...args), wait);
	};
};

export const prependZero = (number: number) => {
	if (number <= 9) return '00000' + number;
	else if (number <= 99) return '0000' + number;
	else if (number <= 999) return '000' + number;
	else if (number <= 9999) return '00' + number;
	else if (number <= 99999) return '0' + number;
	else return number;
};

export const downloadFile = async (url: string, fileType: string) => {
	try {
		const pdfData = await HttpService.get(url, {}, { responseType: 'blob', fullResponse: true });
		const file = new Blob([pdfData], { type: fileType });
		const data = window.URL.createObjectURL(file);
		const link = document.createElement('a');
		link.href = data;
		link.target = '_blank';
		link.click();
	} catch (err) {
		console.error(err);
	}
};

export const isValidURL = (str: string) => {
	const pattern = new RegExp(
		'^(https?:\\/\\/)?' +
			'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
			'((\\d{1,3}\\.){3}\\d{1,3}))' +
			'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
			'(\\?[;&a-z\\d%_.~+=-]*)?' +
			'(\\#[-a-z\\d_]*)?$',
		'i'
	);
	return !!pattern.test(str);
};

export const rawMarkup = (answer: any) => {
	const rawMarkups = answer;
	return { __html: rawMarkups };
};

export const convertDropdownValueToArray = (
	selectedValue: string | string[]
): Array<{ label: string; value: string }> => {
	const values = typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue;
	const data = [];
	if (values) {
		for (const value of values) {
			const temp = {
				label: value,
				value: value
			};
			data.push(temp);
		}
	}
	return data;
};

export const getDropdownValues = (dropdown: IDropdownOptions[]) => {
	return dropdown.map((d: IDropdownOptions) => d.value);
};

export const getDropdownOptions = (values: string[]) => {
	return values.map((value) => ({ label: value, value: value }));
};

export const convertDropdownValueToString = (values: IDropdownOptions[]): string[] => {
	return values.map((item) => item.value);
};

export const formatDateRange = (dateRange: Range) => {
	let startDate;
	let endDate;
	if (dateRange.startDate) {
		startDate = moment(dateRange.startDate).format();
	}
	if (dateRange.endDate) {
		endDate = moment(dateRange.endDate).format();
	}

	return { startDate: startDate, endDate: endDate };
};

export const generateRandomString = (length: number) => {
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	let randomString = '';
	for (let i = 0; i < length; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length);
		randomString += characters.charAt(randomIndex);
	}
	return randomString;
};

export const getAdvanceDate = () => {
	const date = new Date().toISOString();
	const getNextDate = moment(date).add(1, 'day').format('MM-DD-YYYY');
	return getNextDate;
};

export const highlighClassName = (globalSearchValue: string | unknown, compareValue: string) => {
	if (
		globalSearchValue &&
		typeof globalSearchValue === 'string' &&
		compareValue &&
		compareValue.toLocaleLowerCase().includes(globalSearchValue.toLocaleLowerCase())
	) {
		return 'text--highlight';
	}
	return '';
};

//for upload file into s3 bucket
export const uploadFileToS3PreSigned = (
	presignedPostData: IPreSignedData,
	file: File,
	onProgress?: (val: number) => void
): Promise<string> => {
	return new Promise((resolve, reject) => {
		const formData = new FormData();
		Object.keys(presignedPostData.fields).forEach((key) => {
			formData.append(key, presignedPostData.fields[key as PresignedFieldKeys]);
		});
		formData.append('file', file);

		const xhr = new XMLHttpRequest();

		if (onProgress) {
			xhr.upload.onprogress = (e) => {
				if (e.lengthComputable) {
					const progress = Math.round((e.loaded / file.size) * 100);
					onProgress(progress > 100 ? 100 : progress);
				}
			};
		}
		xhr.open('POST', presignedPostData.url, true);
		xhr.send(formData);
		xhr.onload = function () {
			this.status === 204 ? resolve(presignedPostData.cdnUrl) : reject(this.responseText);
		};
	});
};

export const copyToClipboard = (str: string) => {
	const el = document.createElement('textarea');
	el.value = str;
	el.setAttribute('readonly', '');
	el.style.position = 'absolute';
	el.style.left = '-9999px';
	document.body.appendChild(el);
	el.select();
	document.execCommand('copy');
	document.body.removeChild(el);
};

export const checkRequirementsForPrizeStructure = (
	prizeStructure: any,
	currentState: {
		contestantSize: number;
		modesId?: string;
		entryFee: number;
	}
) => {
	if (!prizeStructure.status) {
		return false;
	}
	let check = false;
	for (const key in prizeStructure.requirements) {
		const requirementObj = prizeStructure.requirements[key];
		for (let i = 0; i < requirementObj.length; i++) {
			const currentObj = requirementObj[i];
			const fieldKey = currentObj.field;
			const requiredValue = currentObj.value;
			const checkType = currentObj.checkType;

			if (has(currentState, fieldKey)) {
				const fieldValue = get(currentState, fieldKey, 0);

				switch (checkType) {
					case 'gte':
						check = fieldValue >= requiredValue;
						break;
					case 'equalTo':
						check = fieldValue === requiredValue;
						break;
					case 'in':
						check = requiredValue.includes(fieldValue);
						break;
				}
			}
		}
		if (!check) {
			return false;
		}
	}
	return true;
};

export const getMultiEntries = (multiEntries: any, contestantSize: string) => {
	const multiEntry: IMultiEntry[] = [];
	const addedEntries: Set<any> = new Set();

	for (const key in multiEntries) {
		const multiEntryObject = multiEntries[key];
		const multiEntryReq = multiEntryObject.requirements;
		const multiEntryContestSize = multiEntryReq.contestantSize;

		if (multiEntryObject.isUserAccess && !addedEntries.has(multiEntryObject.entry)) {
			for (let i = 0; i < multiEntryContestSize.length; i++) {
				const { checkType, value } = multiEntryContestSize[i];
				if (
					(checkType === 'lte' && value <= contestantSize) ||
					(checkType === 'gt' && contestantSize > value)
				) {
					multiEntry.push({
						entry: multiEntryObject.entry,
						isUserAccess: true
					});
				}
				addedEntries.add(multiEntryObject.entry);
				break;
			}
		}
	}

	return multiEntry;
};

export const handleUpcomingContest = (startAt: string) => {
	const startDate = new Date(startAt);
	const currentDate = new Date();

	//card is block for joining 1 or 5 minutes before contest start time according to contest type
	const delayTime = Number(process.env.REACT_APP_CONTEST_PRE_START_DELAY);

	return startDate >= currentDate && startDate > new Date(currentDate.getTime() + delayTime * 60000);
};

export const isMobile = (): boolean =>
	/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const isIOSDevice = (): boolean =>
	/iPhone|iPad|iPod|iPad Simulator|iPhone Simulator|iPod Simulator/i.test(navigator.userAgent);

export const prizeFormatter = (value: number) => {
	const formattedValue = new Intl.NumberFormat('de-DE', {
		style: 'currency',
		currency: 'EUR'
	}).format(value);

	// Remove the decimal part
	const result = formattedValue.replace(',00', '').replace(/\./g, '');

	return result;
};

export const handleAuthTrack = async () => {
	let sessionId = localStorage.getItem('device-id');

	if (!sessionId) {
		sessionId = uuidv4();
		localStorage.setItem('device-id', sessionId);
	}

	try {
		sessionId && (await HttpService.post(API_CONFIG.path.authTrack));
	} catch (error) {
		console.error(error);
	}
};

// Determine if color is light or dark to set contrast color
export const getContrastColor = (color: string) => {
	const r = parseInt(color.slice(1, 3), 16);
	const g = parseInt(color.slice(3, 5), 16);
	const b = parseInt(color.slice(5, 7), 16);
	const brightness = (r * 299 + g * 587 + b * 114) / 1000;
	return brightness > 128 ? '#000000' : '#ffffff';
};

// This constant determines if the application is running in a "white-label" mode.
// It checks the environment variable `REACT_APP_IS_WHITE_LABEL_WEB` and sets the value to `true` or `false`.
// When set to `true`, certain branding or feature customizations specific to white-label instances are applied.
export const isWhiteLabel = process.env.REACT_APP_IS_WHITE_LABEL_WEB === 'true';
